// © Microsoft Corporation. All rights reserved.

import { DesignSystem, accentFillRest, accentForegroundRest, isDarkMode, neutralForegroundHint, neutralForegroundRest, neutralLayerCard, neutralLayerCardContainer, neutralOutlineRest } from "@microsoft/fast-components-styles-msft";

import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";

const avatarSize = "32px";
const activityHighlightColor = "#B7DDE9";
const imgSize = "55px";
const alertIconSize = "26px";
const alertColor = "#E22C3B";
const checkColor = "#6BB700";
const messageMaxClampedLines = 2;

export const ProfileActivityStyles = (config: DesignSystem) => ({
    profileActivityListContainer: {
        padding: "1px",
        margin: "0",

        "& .cwt-link.cwt-comment-deleted-message": {
            display: "inline",
            "font-style": "italic",
            "font-weight": "normal",
            color: neutralForegroundHint,

            "& .cwt-icon": {
                margin: "-4px 4px 0",
                color: "inherit",
                display: "inline-flex",
                "vertical-align": "middle"
            }
        },

        "& .cwt-name.cwt-link": {
            color: accentForegroundRest
        }
    },

    profileActivityContainer: {
        display: "flex",
        "min-height": "40px",
        padding: "20px 0",
        background: neutralLayerCard,
        "border-radius": "2px",
        "box-shadow": "0px 1.6px 3.6px rgba(0, 0, 0, 0.13), 0px 0px 2.9px rgba(0, 0, 0, 0.11)",

        "&.new": {
            [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "-2px",
            [applyLocalizedProperty("border-left", "border-right", config.direction)]: "4px solid ",
            "border-color": activityHighlightColor,
            "border-radius": "0 2px 2px 0"
        },
        "& .cwt-start": {
            display: "flex",
            "align-items": "start",
            "justify-content": "center",
            width: "80px",
            "min-width": "80px",
            "text-align": "center",

            "& .cwt-img": {
                "max-width": "30px",
                "max-height": "30px"
            }
        },
        "& .cwt-mid": {
            "min-width": "0px",
            "font-size": "14px",
            color: neutralForegroundRest
        },
        "& .cwt-end": {
            padding: "0 26px",
            [applyLocalizedProperty("margin-left", "margin-right", config.direction)]: "auto",
            "text-align": "center"
        },
        "& .cwt-sub-text": {
            "margin-top": "6px",
            color: isDarkMode(config) ? "#D9D9D9" : neutralForegroundHint,
            "font-size": "12px"
        },
        "& .cwt-link": {
            color: accentFillRest,
            "font-weight": "600"
        },
        "& > .cwt-avatar": {
            height: avatarSize,
            "min-width": avatarSize,
            "max-width": avatarSize
        },
        "& .cwt-body": {
            "& > .cwt-clamp": {
                display: "-webkit-box",
                "-webkit-line-clamp": messageMaxClampedLines,
                "-webkit-box-orient": "vertical",
                overflow: "hidden"
            },
            "& .cwt-show-clamped-btn": {
                color: accentFillRest,
                "font-size": "12px",
                background: neutralLayerCardContainer,
                bottom: "11px",
                display: "inline",
                position: "absolute",
                right: "12px",
                "font-weight": "normal",

                "&:hover": {
                    "text-decoration": "none"
                },

                "&::before": {
                    content: "'...'",
                    display: "inline-block",
                    [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "5px",
                    color: neutralForegroundHint
                }
            }
        },
        "& .cwt-mention-body": {
            position: "relative"
        }
    },

    profileActivityBadgeContainer: {
        "& .cwt-end": {
            height: imgSize
        },
        "& .cwt-badge-img": {
            width: imgSize,
            height: imgSize
        }
    },

    profileActivityFollowContainer: {
    },

    profileActivityReactionContainer: {
        "& .cwt-body": {
            margin: "16px 0 10px",
            padding: "10px 12px",
            position: "relative",
            color: neutralForegroundRest, // added nearest fast-color
            background: neutralLayerCardContainer,
            "overflow-wrap": "anywhere",
            "border-radius": "8px",
            "& *": {
                display: "inline",
                color: isDarkMode(config) ? "#FFFFFF" : "#545454"
            },
            "& .cwt-name.cwt-link": {
                color: accentFillRest,
                "font-weight": "normal",

                "&::before": {
                    content: "'@'",
                    display: "inline"
                }
            }
        },
        "& .cwt-start > .cwt-icon": {
            "& .cwt-icon-vector": {
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "3px",

                "&:last-child-of-type": {
                    [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "0"
                }
            }
        },
        "& .cwt-end": {
            padding: "10px"
        },
        "& > .cwt-icon": {
            display: "flex",
            "align-items": "center",
            "justify-content": "center",
            "margin-top": "2px"
        },
        "& .cwt-body-link:hover": {
            "text-decoration": "none",
            color: neutralForegroundRest
        }
    },

    profileActivityReplyContainer: {
        "& .cwt-body": {
            margin: "16px 0 10px",
            padding: "10px 12px",
            background: neutralLayerCardContainer,
            position: "relative",
            color: neutralForegroundRest,
            "border-radius": "8px",
            "& > *": {
                display: "inline"
            },

            "& .cwt-mention.cwt-link": {
                color: accentFillRest,
                display: "inline-flex",
                "font-weight": "normal",

                "&::before": {
                    content: "'@'",
                    display: "inline"
                }
            }
        },

        "& .cwt-end": {
            padding: "10px"
        },

        "& .cwt-body-link": {
            "& *": {
                display: "inline"
            },
            "&:hover": {
                "text-decoration": "none",
                color: neutralForegroundRest
            },
            "& .cwt-message": {
                "word-break": "break-word"
            }
        }
    },

    profileActivityTitleContainer: {
        "& .cwt-text": {
            "line-height": "22px"
        },
        "& .cwt-title-label": {
            margin: "0 10px",
            padding: "4px 8px",
            "font-size": "14px",
            "font-weight": "600",
            color: neutralLayerCard,
            "border-radius": "4px",
            "letter-spacing": applyLocalizedProperty("-0.02em", "0", config.direction),
            "white-space": "nowrap"
        }
    },

    profileActivityAlertContainer: {
        "& .cwt-icon.cwt-alert-icon": {
            display: "flex",
            "align-items": "center",
            "justify-content": "center",
            width: alertIconSize,
            height: alertIconSize,
            color: "#FFFFFF",
            background: alertColor,
            "border-radius": "50%"
        },
        "& .cwt-icon.cwt-alert-icon.cwt-check-icon": {
            background: checkColor
        },
        "& .cwt-preview": {
            margin: "10px 0",
            padding: "10px 12px",
            color: "#777777",
            background: neutralLayerCardContainer,
            "border-radius": "8px"
        },
        "& .cwt-buttons": {
            display: "flex",
            padding: "7px 0",
            "flex-wrap": "wrap",

            "& .cwt-fast-button": {
                "min-width": "130px",
                "margin-bottom": "5px",
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "9px"
            }
        },
        "& .cwt-end": {
            padding: "10px"
        }
    },

    profileReactionActivityMiniBreakdownPopover: {
        [applyLocalizedProperty("left", "right", config.direction)]: "-36px"
    }
});